import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Sparkles,
  MessageCircle,
  Search,
  CheckCircle,
  Edit3,
  X,
  Save,
  Filter,
  Trash2,
  MoreVertical,
  Database,
  ChevronDown
  ,AlertTriangle, ArrowRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ApiService from '../../services/ApiService';
import useBillingStore from '../../store/useBillingStore';

function AnswerOptimization() {
  const location = useLocation();
  const [faqs, setFaqs] = useState([]);


  // edits beign from here
  const [showFilterKnowledgeVaultModal, setShowFilterKnowledgeVaultModal] = useState(false);

  const [knowledgeVault, setKnowledgeVault] = useState([]);

  const [localSelectedKVIds, setLocalSelectedKVIds] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedKVIds, setSelectedKVIds] = useState([]);
  const [listView, setListView] = useState(true);

  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState({});
  const [editing, setEditing] = useState({});
  const [editData, setEditData] = useState({});
  const [selectedOptimization, setSelectedOptimization] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState({});
  const [openLeft, setOpenLeft] = useState({});
  const { billingInfo, fetchBillingInfo } = useBillingStore();

  const usageLimitReached = billingInfo &&
  billingInfo.limits &&
  billingInfo.usage &&
  billingInfo.usage.tokens >= billingInfo.limits.tokens;

  const getExceededDetails = () => {
    // Only show tokens for Answer Optimization
    if (!billingInfo || !billingInfo.limits || !billingInfo.usage) return [];
    const details = [];
    const tokensUsed = billingInfo.usage?.tokens;
    const tokensLimit = billingInfo.limits?.tokens;
    if (typeof tokensUsed === 'number' && typeof tokensLimit === 'number' && tokensUsed >= tokensLimit) {
      details.push({ key: 'tokens', title: 'AI Tokens', used: tokensUsed, limit: tokensLimit, why: 'Your AI token quota is exhausted — FAQ optimization and AI transformations are paused. Upgrade to resume optimizations.' });
    }
    return details;
  };

  // State for view FAQ modal
  // State for edit FAQ modal
  const [editFAQModal, setEditFAQModal] = useState({
    show: false,
    faq: null,
    question: '',
    answer: '',
    submitting: false,
  });

  // State for expanded FAQs in list view
  const [expandedFAQs, setExpandedFAQs] = useState({});

  const optimizationTypes = [
    { value: 'clearer', label: 'Make Clearer', description: 'Simplify language and improve clarity' },
    { value: 'concise', label: 'Make Concise', description: 'Reduce length while keeping key information' },
    { value: 'detailed', label: 'Add Detail', description: 'Provide more comprehensive information' },
    { value: 'friendly', label: 'More Friendly', description: 'Improve tone to be more approachable' },
    { value: 'professional', label: 'More Professional', description: 'Enhance formality and professionalism' },
    { value: 'technical', label: 'More Technical', description: 'Add technical depth and specificity' }
  ];

  useEffect(() => {
    loadFAQs();
    fetchBillingInfo();
  }, [fetchBillingInfo]);

  // Handle URL search parameters for FAQ navigation from Knowledge Vault
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      // Clear the URL parameter to clean up the address bar
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [location.search]);

  

  const loadFAQs = async () => {
    try {
      setLoading(true);

      let taskArray = []
      taskArray.push(ApiService.getFAQs());
      taskArray.push(ApiService.getContentList());

      const [faqData, kvData] = await Promise.all(taskArray);

      setFaqs(faqData.faqs || []);
      setKnowledgeVault(kvData.content || []);

    } catch (error) {
      console.error('Failed to load FAQs:', error);
      toast.error('Failed to load FAQs');
    }
    finally {
      setLoading(false);
    }
  };

  const handleOptimize = async (faqId) => {
    if (!selectedOptimization) {
      toast.error('Please select an optimization type');
      return;
    }

    try {
      setOptimizing({ ...optimizing, [faqId]: true });

      const result = await ApiService.optimizeFAQ(faqId, selectedOptimization);

      if (result.success) {
        toast.success('FAQ optimized successfully!');
        // Update the FAQ in the list
        setFaqs(faqs.map(faq =>
          faq.faq_id === faqId
            ? { ...faq, answer: result.optimized_answer }
            : faq
        ));
      } else {
        toast.error(result.message || 'Failed to optimize FAQ');
      }
    } catch (error) {
      console.error('Optimization error:', error);
      toast.error('Failed to optimize FAQ');
    } finally {
      setOptimizing({ ...optimizing, [faqId]: false });
    }
  };

  const handleEdit = (faq) => {
    if (!listView) {
      // Keep inline editing for card view
      setEditing({ ...editing, [faq.faq_id]: true });
      setEditData({
        ...editData,
        [faq.faq_id]: {
          question: faq.question,
          answer: faq.answer
        }
      });
    } else {
      // Open modal for list view
      setEditFAQModal({
        show: true,
        faq: faq,
        question: faq.question,
        answer: faq.answer,
        submitting: false,
      });
    }
  };

  const handleCancelEdit = (faqId) => {
    setEditing({ ...editing, [faqId]: false });
    setEditData({ ...editData, [faqId]: undefined });
  };

  const handleSaveEdit = async (faqId) => {
    try {
      const data = editData[faqId];
      if (!data) return;

      const result = await ApiService.updateFAQ(faqId, data.question, data.answer);

      if (result.success) {
        toast.success('FAQ updated successfully!');
        // Update the FAQ in the list
        setFaqs(faqs.map(faq =>
          faq.faq_id === faqId
            ? { ...faq, question: data.question, answer: data.answer }
            : faq
        ));
        setEditing({ ...editing, [faqId]: false });
        setEditData({ ...editData, [faqId]: undefined });
      } else {
        toast.error('Failed to update FAQ');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update FAQ');
    }
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ? This action cannot be undone.')) {
      return;
    }

    try {
      await ApiService.deleteFAQ(faqId);
      // If we reach here, deletion was successful
      toast.success('FAQ deleted successfully');
      // Remove the FAQ from the local state
      setFaqs(faqs.filter(faq => faq.faq_id !== faqId));
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete FAQ');
    }
  };

  // Helper to handle menu open and dynamic positioning
  const handleMobileMenuOpen = (faqId) => {
    setMobileMenuOpen(prev => ({ ...prev, [faqId]: !prev[faqId] }));
    setTimeout(() => {
      const menu = document.getElementById(`faq-menu-${faqId}`);
      if (menu) {
        const rect = menu.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
          setOpenLeft(prev => ({ ...prev, [faqId]: true }));
        } else {
          setOpenLeft(prev => ({ ...prev, [faqId]: false }));
        }
      }
    }, 0);
  };

  // Handle edit modal functions
  const handleEditModalSave = async () => {
    try {
      setEditFAQModal(prev => ({ ...prev, submitting: true }));

      const result = await ApiService.updateFAQ(
        editFAQModal.faq.faq_id, 
        editFAQModal.question, 
        editFAQModal.answer
      );

      if (result.success) {
        toast.success('FAQ updated successfully!');
        // Update the FAQ in the list
        setFaqs(faqs.map(faq =>
          faq.faq_id === editFAQModal.faq.faq_id
            ? { ...faq, question: editFAQModal.question, answer: editFAQModal.answer }
            : faq
        ));
        // Close modal
        setEditFAQModal({
          show: false,
          faq: null,
          question: '',
          answer: '',
          submitting: false,
        });
      } else {
        toast.error('Failed to update FAQ');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update FAQ');
    } finally {
      setEditFAQModal(prev => ({ ...prev, submitting: false }));
    }
  };

  const handleEditModalClose = () => {
    setEditFAQModal({
      show: false,
      faq: null,
      question: '',
      answer: '',
      submitting: false,
    });
  };

  // Toggle FAQ expansion in list view
  const toggleFAQExpansion = (faqId) => {
    setExpandedFAQs(prev => ({
      ...prev,
      [faqId]: !prev[faqId]
    }));
  };


  const getFilteredFAQs = () => {
    
    return faqs.filter(faq => {
      const query = searchQuery.toLowerCase();

      const hasSearchMatch =
        !searchQuery ||
        faq.question.toLowerCase().includes(query) ||
        faq.answer.toLowerCase().includes(query);

      const hasKVMatch =
        selectedKVIds.length === 0 ||
        selectedKVIds.includes(faq.content_id);

      return hasSearchMatch && hasKVMatch;
    });
  };


  const handleCheckboxToggle = (contentId) => {
    setLocalSelectedKVIds((prevSelected) =>
      prevSelected.includes(contentId)
        ? prevSelected.filter((id) => id !== contentId)    
        : [...prevSelected, contentId]                     
    );
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Answer Optimization</h2>
          <p className="text-gray-600 mt-2">Use AI to improve your FAQ answers for better customer experience</p>
        </div>
      </div>      {/* Optimization Controls */}
      {/* Permanent Quota Banner */}
      {(() => {
        const exceeded = getExceededDetails();
        if (!exceeded || exceeded.length === 0) return null;
        return (
          <div className="mt-4 rounded-xl border border-red-200 bg-red-50/60 backdrop-blur p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center border border-red-200">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-red-900">
                      Usage limit reached
                    </h4>
                    <p className="text-sm text-red-700/90 mt-0.5">
                      You've reached the following monthly quota{exceeded.length>1?'s':''}. Upgrade to increase limits and continue using AI-powered features.
                    </p>
                  </div>
                  <a
                    href="/billing"
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 text-white px-3 py-2 text-sm font-semibold shadow-sm hover:bg-red-700 transition-colors"
                  >
                    View billing & upgrade
                    <ArrowRight className="h-4 w-4" />
                  </a>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {exceeded.map((d) => {
                    const percent = d.limit > 0 ? Math.min(100, Math.round((d.used / d.limit) * 100)) : 100;
                    return (
                      <div key={d.key} className="rounded-lg border border-red-200 bg-white/60 p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium uppercase tracking-wide text-red-700/80">
                            {d.title}
                          </span>
                          <span className="text-[11px] font-semibold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                            {d.used} / {d.limit}
                          </span>
                        </div>
                        <div className="mt-2 h-2 rounded-full bg-red-100 border border-red-200 overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-red-500 to-red-600"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <p className="mt-2 text-xs text-red-700/90 leading-relaxed">
                          {d.why}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Optimization Settings</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {optimizationTypes.map((type) => (
              <div key={type.value} className="relative">
                <input
                  type="radio"
                  id={type.value}
                  name="optimization"
                  value={type.value}
                  checked={selectedOptimization === type.value}
                  onChange={(e) => setSelectedOptimization(e.target.value)}
                  className="sr-only"
                />
                <label
                  htmlFor={type.value}
                  className={`block p-4 border rounded-xl cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 ${selectedOptimization === type.value
                    ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 shadow-lg'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900">{type.label}</h4>
                    {selectedOptimization === type.value && (
                      <CheckCircle className="h-5 w-5 text-blue-500" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-2 leading-relaxed">{type.description}</p>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>


      {/* Filter Bar */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="p-3 sm:p-4">
          {/* Mobile Layout (phones) */}
          <div className="flex flex-col gap-3 sm:hidden">
            {/* Search Input */}
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
              />
            </div>

            {/* Knowledge Vault Multi-Select */}
            <div className="relative w-full">
              <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <button
                onClick={() => setShowFilterKnowledgeVaultModal(true)}
                className="w-full pl-12 pr-4 h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 text-sm text-left text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              >
                {localSelectedKVIds.length > 0 
                  ? `${localSelectedKVIds.length} Selected`
                  : 'Filter By Knowledge Vault'
                }
              </button>
            </div>

            {/* Button Group - Grid for mobile */}
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  setSelectedKVIds(localSelectedKVIds);
                }}
                className="h-11 px-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-sm font-medium"
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setLocalSelectedKVIds([]);
                  setSearchQuery('');
                  setSelectedKVIds([]);
                }}
                className="h-11 px-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 text-sm font-medium"
              >
                Clear
              </button>
              <button
                onClick={() => setListView((prev) => !prev)}
                className={`h-11 px-2 rounded-xl border border-gray-300 text-sm font-medium transition-all duration-200 flex items-center justify-center ${listView ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                {listView ? 'Cards' : 'List'}
              </button>
            </div>
          </div>

          {/* Tablet Layout (iPad) */}
          <div className="hidden sm:flex lg:hidden flex-col gap-4 w-full">
            {/* Top Row: Search and Filter */}
            <div className="flex gap-4">
              {/* Search Input */}
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search FAQs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                />
              </div>

              {/* Knowledge Vault Multi-Select */}
              <div className="relative flex-1">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <button
                  onClick={() => setShowFilterKnowledgeVaultModal(true)}
                  className="w-full pl-12 pr-4 h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 text-base text-left text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                >
                  {localSelectedKVIds.length > 0 
                    ? `${localSelectedKVIds.length} Knowledge Vault${localSelectedKVIds.length === 1 ? '' : 's'} Selected`
                    : 'Filter By Knowledge Vault'
                  }
                </button>
              </div>
            </div>

            {/* Bottom Row: Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setSelectedKVIds(localSelectedKVIds);
                }}
                className="h-12 px-8 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-base font-medium min-w-[120px]"
              >
                Apply Filters
              </button>
              <button
                onClick={() => {
                  setLocalSelectedKVIds([]);
                  setSearchQuery('');
                  setSelectedKVIds([]);
                }}
                className="h-12 px-8 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 text-base font-medium min-w-[120px]"
              >
                Clear Filters
              </button>
              <button
                onClick={() => setListView((prev) => !prev)}
                className={`h-12 px-8 rounded-xl border border-gray-300 text-base font-medium transition-all duration-200 flex items-center justify-center whitespace-nowrap min-w-[120px] ${listView ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
               
                {listView ? 'Card View' : 'List View'}
              </button>
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex lg:flex-row items-stretch gap-4 w-full">
            {/* Search Input */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
              />
            </div>

            {/* Knowledge Vault Multi-Select */}
            <div className="relative flex-1 w-full max-w-xs">
              <button
                onClick={() => setShowFilterKnowledgeVaultModal(true)}
                className="w-full pl-12 pr-4 h-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 text-base"
              >
                {/* <Mail className="h-5 w-5 mr-2" /> */}
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                Filter By Knowledge Vault
              </button>
            </div>

            {/* Button Group */}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setSelectedKVIds(localSelectedKVIds);
                }}
                className="h-12 px-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-sm hover:from-blue-600 hover:to-blue-700 transition-all duration-200 text-base font-medium"
              >
                Apply
              </button>
              <button
                onClick={() => {
                  setLocalSelectedKVIds([]);
                  setSearchQuery('');
                  setSelectedKVIds([]);
                }}
                className="h-12 px-6 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl shadow-sm hover:bg-gray-200 hover:border-gray-400 transition-all duration-200 text-base font-medium"
              >
                Clear
              </button>
              <button
                onClick={() => setListView((prev) => !prev)}
                className={`h-12 px-6 rounded-xl border border-gray-300 text-base font-medium transition-all duration-200 flex items-center justify-center whitespace-nowrap ${listView ? 'bg-blue-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
              >
                {listView ? 'Card View' : 'List View'}
              </button>
            </div>
          </div>
        </div>
      </div>


      {/*multiple knowledge vault*/}
      {showFilterKnowledgeVaultModal && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
          <div className="fixed inset-0 flex items-center justify-center p-5">
            <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md transform transition-all duration-300 scale-100">
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Select Knowledge Vaults
                  </h3>
                  <button
                    onClick={() => {
                      setShowFilterKnowledgeVaultModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* onSubmit={handleDownloadCSVSubmit} */}

                <form className="space-y-5">
                  {/* Checkbox UI */}
                  <div className="p-4 border rounded-xl w-full max-w-sm bg-white shadow-sm">
                    {/* <p className="mb-2 font-semibold">Filter by Status:</p> */}

                    <div className="flex items-center space-x-4 mb-3">

                      <button
                        type="button"
                        onClick={() => setLocalSelectedKVIds([])}
                        className="text-sm text-red-500 hover:underline"
                      >
                        Clear All
                      </button>
                    </div>

                    {/* Checkbox elements */}
                    <div className="space-y-2 max-h-64 overflow-y-auto overscroll-contain">

                      {knowledgeVault?.length > 0 ? (
                        knowledgeVault.map((kv) => (
                          <label key={kv.content_id} className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              value={kv.content_id}
                              checked={localSelectedKVIds.includes(kv.content_id)}
                              onChange={() => handleCheckboxToggle(kv.content_id)}
                              className="accent-blue-500"
                            />
                            <span className="capitalize">{kv.title}</span>

                          </label>
                        ))
                      ) : (
                        <h4>Something went wrong while fetching knowledge vaults</h4>
                      )}
                    </div>
                  </div>


                  <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            
                    <button
                      type="button"
                      onClick={() => {
                        setShowFilterKnowledgeVaultModal(false);
                      }}
                      className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-xl transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg hover:shadow-xl"
                    >
                      Done
                    </button>

                  </div>
                </form>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQ List */}
      {getFilteredFAQs().length === 0 ? (
        <div className="text-center py-16">
          <div className="relative mx-auto w-24 h-24 mb-6">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
            <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {faqs.length === 0 ? 'No FAQs available' : 'No FAQs match your search'}
          </h3>
          <p className="text-gray-500 mb-8 max-w-md mx-auto">
            {faqs.length === 0
              ? 'Upload content in the Knowledge Vault to generate FAQs'
              : 'Try adjusting your search terms'
            }
          </p>
        </div>
      ) : (
        listView ? (
          // FAQ-style List View
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200/50 w-full">
            <div className="p-6">
              {getFilteredFAQs().map((faq, index) => (
                <div key={faq.faq_id} className="border-b border-gray-100 last:border-b-0">
                  <button
                    onClick={() => toggleFAQExpansion(faq.faq_id)}
                    className="w-full text-left py-4 px-2 flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors group"
                  >
                    <div className="flex-1 pr-4">
                      <h3 className="text-sm md:text-base font-medium text-gray-900 group-hover:text-blue-600 transition-colors">
                        {faq.question}
                      </h3>
                    </div>
                    <ChevronDown 
                      className={`h-5 w-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                        expandedFAQs[faq.faq_id] ? 'transform rotate-180' : ''
                      }`} 
                    />
                  </button>
                  
                  {/* Expanded Answer Section */}
                  {expandedFAQs[faq.faq_id] && (
                    <div className="px-2 pb-4 border-t border-gray-100 mt-2">
                      <div className="pt-4">
                        {/* Answer Content */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Answer:</h4>
                          <div className="prose prose-gray max-w-none">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                // Headings
                                h1: ({ node, ...props }) => <h1 className="text-lg font-bold text-gray-900 mb-3 mt-4 border-b border-gray-200 pb-1" {...props} />,
                                h2: ({ node, ...props }) => <h2 className="text-base font-semibold text-gray-800 mb-2 mt-3" {...props} />,
                                h3: ({ node, ...props }) => <h3 className="text-sm font-medium text-gray-800 mb-2 mt-3" {...props} />,
                                h4: ({ node, ...props }) => <h4 className="text-sm font-medium text-gray-700 mb-1 mt-2" {...props} />,

                                // Paragraphs
                                p: ({ node, ...props }) => <p className="text-gray-900 leading-relaxed mb-2 text-sm" {...props} />,

                                // Lists
                                ul: ({ node, ...props }) => <ul className="list-disc list-inside space-y-1 mb-3 ml-2" {...props} />,
                                ol: ({ node, ...props }) => <ol className="list-decimal list-inside space-y-1 mb-3 ml-2" {...props} />,
                                li: ({ node, ...props }) => <li className="text-gray-900 leading-relaxed text-sm" {...props} />,

                                // Links
                                a: ({ node, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors text-sm" {...props} />,

                                // Code
                                code: ({ node, inline, ...props }) =>
                                  inline
                                    ? <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded font-mono text-xs" {...props} />
                                    : <code className="block bg-gray-800 text-gray-100 p-3 rounded-lg font-mono text-xs overflow-x-auto" {...props} />,

                                pre: ({ node, ...props }) => <pre className="bg-gray-800 rounded-lg mb-3 overflow-hidden" {...props} />,

                                // Blockquotes
                                blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-300 pl-3 italic text-gray-700 bg-blue-50 py-2 rounded-r-lg mb-3 text-sm" {...props} />,

                                // Tables
                                table: ({ node, ...props }) => <div className="overflow-x-auto mb-3"><table className="min-w-full border-collapse border border-gray-300 text-sm" {...props} /></div>,
                                th: ({ node, ...props }) => <th className="border border-gray-300 bg-gray-100 px-3 py-1.5 text-left font-semibold text-gray-800 text-xs" {...props} />,
                                td: ({ node, ...props }) => <td className="border border-gray-300 px-3 py-1.5 text-gray-900 text-xs" {...props} />,

                                // Strong and emphasis
                                strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                                em: ({ node, ...props }) => <em className="italic text-gray-800" {...props} />,

                                // Horizontal rule
                                hr: ({ node, ...props }) => <hr className="border-gray-300 my-4" {...props} />,
                              }}
                            >
                              {faq.answer}
                            </ReactMarkdown>
                          </div>
                        </div>
                        
                        {/* Action Buttons - Match Card View Styling */}
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(faq.faq_id);
                            }}
                            className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all duration-200"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(faq);
                            }}
                            className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-blue-600 bg-gray-100 hover:bg-blue-50 rounded-xl transition-all duration-200"
                          >
                            <Edit3 className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (usageLimitReached) {
                                toast.error(
                                  'You have exceeded your token usage limit. Please upgrade your plan to use the optimize feature.'
                                );
                              } else {
                                handleOptimize(faq.faq_id);
                              }
                            }}
                            disabled={!selectedOptimization || optimizing[faq.faq_id]}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                          >
                            {optimizing[faq.faq_id] ? (
                              <>
                                <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                                Optimizing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                Optimize
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {/* Show helpful message if no FAQs */}
              {getFilteredFAQs().length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">
                    {faqs.length === 0 
                      ? 'No FAQs available. Upload content to generate FAQs.' 
                      : 'No FAQs match your search criteria.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Card View
          <div className="space-y-6">
          {getFilteredFAQs().map((faq) => (
            <div key={faq.faq_id} className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1 relative">
              <div className="p-6">
                {/* Three-dot menu button absolutely positioned inside the card */}
                <div className="sm:hidden">
                  <button
                    onClick={() => handleMobileMenuOpen(faq.faq_id)}
                    className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 text-gray-500 z-20"
                    style={{ zIndex: 20 }}
                  >
                    <MoreVertical className="h-5 w-5" />
                  </button>
                  {mobileMenuOpen[faq.faq_id] && (
                    <div
                      id={`faq-menu-${faq.faq_id}`}
                      className={`absolute mt-2 w-36 max-w-[calc(100vw-32px)] min-w-0 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-x-auto ${openLeft[faq.faq_id] ? 'left-4 right-auto' : 'right-4 left-auto'}`}
                      style={{ top: '2.5rem' }}
                    >
                      <button
                        onClick={() => { setMobileMenuOpen({}); handleEdit(faq); }}
                        className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-blue-50"
                      >
                        <Edit3 className="h-4 w-4 mr-2" /> Edit
                      </button>
                      <button
                        onClick={() => { setMobileMenuOpen({}); handleOptimize(faq.faq_id); }}
                        disabled={!selectedOptimization || optimizing[faq.faq_id]}
                        className="w-full flex items-center px-4 py-2 text-gray-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Sparkles className="h-4 w-4 mr-2" /> Optimize
                      </button>
                      <button
                        onClick={() => { setMobileMenuOpen({}); handleDelete(faq.faq_id); }}
                        className="w-full flex items-center px-4 py-2 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </button>
                    </div>
                  )}
                </div>
                {/* End mobile menu button */}
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    {editing[faq.faq_id] ? (
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Question:</label>
                          <input
                            type="text"
                            value={editData[faq.faq_id]?.question || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              [faq.faq_id]: {
                                ...editData[faq.faq_id],
                                question: e.target.value
                              }
                            })}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Answer:</label>
                          <textarea
                            value={editData[faq.faq_id]?.answer || ''}
                            onChange={(e) => setEditData({
                              ...editData,
                              [faq.faq_id]: {
                                ...editData[faq.faq_id],
                                answer: e.target.value
                              }
                            })}
                            rows={4}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 resize-none"
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleSaveEdit(faq.faq_id)}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            Save
                          </button>
                          <button
                            onClick={() => handleCancelEdit(faq.faq_id)}
                            className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-200"
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </button>
                        </div>
                      </div>) : (
                      <>
                        <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-200 pr-12 sm:pr-0">{faq.question}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-500 mb-4">
                          <div className="flex items-center">
                            <span className="font-medium">Confidence:</span>
                            <div className="ml-2 flex items-center">
                              <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full"
                                  style={{ width: `${Math.round((faq.confidence || 0.8) * 100)}%` }}
                                ></div>
                              </div>
                              <span className="ml-2 font-medium text-gray-700">{Math.round((faq.confidence || 0.8) * 100)}%</span>
                            </div>
                          </div>
                          <div className="text-gray-400">•</div>
                          <span>Created: {new Date(faq.created_at).toLocaleDateString()}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex space-x-2 ml-4">
                    {/* Desktop buttons */}
                    <div className="hidden sm:flex space-x-2">
                      {!editing[faq.faq_id] && (
                        <button
                          onClick={() => handleDelete(faq.faq_id)}
                          className="text-gray-400 hover:text-red-500 p-2 rounded-lg hover:bg-red-50 transition-all duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                      {!editing[faq.faq_id] && (
                        <button
                          onClick={() => handleEdit(faq)}
                          className="inline-flex items-center px-3 py-2 text-gray-600 hover:text-blue-600 bg-gray-100 hover:bg-blue-50 rounded-xl transition-all duration-200"
                        >
                          <Edit3 className="h-4 w-4 mr-1" />
                          Edit
                        </button>
                      )}
                      {!editing[faq.faq_id] && (
                        <button
                          onClick={() => {
                            if (usageLimitReached) {
                              toast.error(
                                'You have exceeded your token usage limit. Please upgrade your plan to use the optimize feature.'
                              );
                            } else {
                              handleOptimize(faq.faq_id);
                            }
                          }}
                          disabled={!selectedOptimization || optimizing[faq.faq_id]}
                          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                        >
                          {optimizing[faq.faq_id] ? (
                            <>
                              <div className="animate-spin -ml-1 mr-2 h-4 w-4 border-2 border-white/30 border-t-white rounded-full"></div>
                              Optimizing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 mr-2" />
                              Optimize
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {!editing[faq.faq_id] && (
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Answer:</h4>                    <div className="prose prose-gray max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          // Headings
                          h1: ({ node, ...props }) => <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6 border-b border-gray-200 pb-2" {...props} />,
                          h2: ({ node, ...props }) => <h2 className="text-xl font-semibold text-gray-800 mb-3 mt-5" {...props} />,
                          h3: ({ node, ...props }) => <h3 className="text-lg font-medium text-gray-800 mb-2 mt-4" {...props} />,
                          h4: ({ node, ...props }) => <h4 className="text-base font-medium text-gray-700 mb-2 mt-3" {...props} />,

                          // Paragraphs
                          p: ({ node, ...props }) => <p className="text-gray-900 leading-relaxed mb-3" {...props} />,

                          // Lists
                        ul: ({node, ...props}) => <ul style={{marginLeft: 24, marginBottom: 8, listStyleType: 'disc'}} {...props} />,
                            ol: ({node, ...props}) => <ol style={{marginLeft: 24, marginBottom: 8, listStyleType: 'decimal'}} {...props} />,
                            li: ({node, ...props}) => <li style={{marginBottom: 4}} {...props} />,

                          // Links
                          a: ({ node, ...props }) => <a className="text-blue-600 hover:text-blue-800 underline decoration-blue-300 hover:decoration-blue-500 transition-colors" {...props} />,

                          // Code
                          code: ({ node, inline, ...props }) =>
                            inline
                              ? <code className="bg-gray-200 text-gray-800 px-1.5 py-0.5 rounded font-mono text-sm" {...props} />
                              : <code className="block bg-gray-800 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto" {...props} />,

                          pre: ({ node, ...props }) => <pre className="bg-gray-800 rounded-lg mb-4 overflow-hidden" {...props} />,

                          // Blockquotes
                          blockquote: ({ node, ...props }) => <blockquote className="border-l-4 border-blue-300 pl-4 italic text-gray-700 bg-blue-50 py-2 rounded-r-lg mb-4" {...props} />,

                          // Tables
                          table: ({ node, ...props }) => <div className="overflow-x-auto mb-4"><table className="min-w-full border-collapse border border-gray-300" {...props} /></div>,
                          th: ({ node, ...props }) => <th className="border border-gray-300 bg-gray-100 px-4 py-2 text-left font-semibold text-gray-800" {...props} />,
                          td: ({ node, ...props }) => <td className="border border-gray-300 px-4 py-2 text-gray-900" {...props} />,

                          // Strong and emphasis
                          strong: ({ node, ...props }) => <strong className="font-semibold text-gray-900" {...props} />,
                          em: ({ node, ...props }) => <em className="italic text-gray-800" {...props} />,

                          // Horizontal rule
                          hr: ({ node, ...props }) => <hr className="border-gray-300 my-6" {...props} />,
                        }}
                      >
                        {faq.answer}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}
      
      {/* Stats */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="flex items-center">
            <h3 className="text-xl font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Optimization Statistics</h3>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200/50">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{faqs.length}</div>
              <div className="text-sm text-gray-600 font-medium">Total FAQs</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200/50">
              <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                {faqs.filter(faq => (faq.confidence || 0.8) > 0.8).length}
              </div>
              <div className="text-sm text-gray-600 font-medium">High Confidence</div>
            </div>
            <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-200/50">
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">
                {faqs.filter(faq => faq.optimization_type).length}
              </div>
              <div className="text-sm text-gray-600 font-medium">Optimized</div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit FAQ Modal */}
      {editFAQModal.show && editFAQModal.faq && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Edit FAQ</h2>
                <button
                  onClick={handleEditModalClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Question Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-blue-700 mb-2">
                  Question
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <textarea
                    value={editFAQModal.question}
                    onChange={(e) => setEditFAQModal(prev => ({ ...prev, question: e.target.value }))}
                    className="w-full bg-transparent resize-none border-none focus:outline-none text-gray-800"
                    rows="3"
                    placeholder="Enter question..."
                  />
                </div>
              </div>

              {/* Answer Section */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-green-700 mb-2">
                  Answer
                </label>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <textarea
                    value={editFAQModal.answer}
                    onChange={(e) => setEditFAQModal(prev => ({ ...prev, answer: e.target.value }))}
                    className="w-full bg-transparent resize-none border-none focus:outline-none text-gray-800"
                    rows="8"
                    placeholder="Enter answer..."
                  />
                </div>
              </div>

              {/* Metadata */}
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-gray-600">Knowledge Vault ID:</span>
                  <p className="text-sm text-gray-800 mt-1">{editFAQModal.faq.knowledge_vault_id || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-600">Created:</span>
                  <p className="text-sm text-gray-800 mt-1">
                    {editFAQModal.faq.created_at ? new Date(editFAQModal.faq.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={handleEditModalClose}
                  className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditModalSave}
                  disabled={editFAQModal.submitting || !editFAQModal.question.trim() || !editFAQModal.answer.trim()}
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 font-medium flex items-center gap-2"
                >
                  {editFAQModal.submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnswerOptimization;